<script setup lang="ts">
import {ref, shallowRef, onUnmounted, onMounted, nextTick, watch, computed} from 'vue'
import {useHotkeys} from '~/lib/settings/hotkeys'
import {toast} from 'vue-sonner'
import * as Sentry from '@sentry/nuxt'
import {listen, type UnlistenFn} from '@tauri-apps/api/event'
import {isTauriRuntime} from '~/lib/platform/runtime'
import {useGroupsStore} from '~/stores/groups'
import {useUserStore} from '~/stores/user'
import {resolveMediaUrl} from '~/lib/mediaCache'
import {liveKitManager} from '~/lib/livekit/manager'
import {
  IosBroadcastCancelledError,
  isIosScreenBroadcastActive,
  isIosScreenBroadcastAvailable,
  startIosScreenBroadcast,
  stopIosScreenBroadcast,
} from '~/lib/livekit/iosScreenBroadcast'
import {PermissionFlag} from '~/lib/permissions'
import {storeToRefs} from 'pinia'
import {
  Track,
  RoomEvent,
} from 'livekit-client'
import {Minimize2, Focus, LayoutGrid, ChevronDown} from 'lucide-vue-next'

// Sub-components
import CallHeader from './CallHeader.vue'
import DebugPanel from './DebugPanel.vue'
import CallControls from './CallControls.vue'
import DeviceModal from './DeviceModal.vue'
import PermissionModal from './PermissionModal.vue'
import CallStage from './CallStage.vue'
import ScreenShareModal from './ScreenShareModal.vue'
import VoiceBackground from "~/components/voice-ui/Background/VoiceBackground.vue";
import {loadAppSettings, patchAppSettings, type CallViewMode} from '~/lib/settings/appSettings'
import {
  ContextMenu,
  ContextMenuCheckboxItem,
  ContextMenuContent,
  ContextMenuTrigger
} from '@/components/ui/context-menu'
import {ToggleGroup, ToggleGroupItem} from '@/components/ui/toggle-group'
import {useMobileDrawerCloseOnBack} from '~/composables/useMobileDrawerBackNav'
import {usePlatform} from "~/composables/usePlatform.ts";

const props = defineProps<{
  channelId: string
  channelName: string
  groupName: string
  userName: string
}>()

const groupsStore = useGroupsStore()
const userStore = useUserStore()
// Declared first: setup code below reads it directly (e.g. `useMobileDrawerCloseOnBack`), and a
// `const` used before its line throws in `<script setup>`, killing the whole call view.
const {isMobile} = usePlatform()
const {
  participants,
  roomState,
  activeRoom,
  isMicMuted,
  isDeafened,
  isCameraEnabled,
  isScreenShareEnabled,
  isNoiseSuppressionEnabled,
  isNoiseSuppressionPending,
  isEchoCancellationEnabled,
  isSelfMonitoring,
  micVolume,
  speakerVolume,
  micSensitivity,
  videoQuality,
  screenShareQuality,
  screenShareFps,
  isScreenShareAudioEnabled,
  isReconnecting,
  micPermission,
  cameraPermission,
} = storeToRefs(groupsStore)

// Checked in the background as soon as the call view mounts (i.e. right when you join a
// channel) — `refreshMediaPermissions()` never itself prompts, it just reads the OS/browser's
// current grant state so the blocking modal below and the disabled buttons in `CallControls`
// reflect reality immediately instead of only after the first failed `getUserMedia` call.
void groupsStore.refreshMediaPermissions()

const isMicPermissionDenied = computed(() => micPermission.value === 'denied')
const isCameraPermissionDenied = computed(() => cameraPermission.value === 'denied')
const permissionModalOpen = computed(() => isMicPermissionDenied.value || isCameraPermissionDenied.value)

// Group-role gate, separate from the OS/browser permission checks above — a role without "Speak"
// or "Video" should never be able to turn the mic/camera/screen-share on at all, regardless of OS
// grants. Resolved by this channel's own group, not `selectedGroupId` (the call can stay open
// while browsing a different group in the sidebar).
const callGroupId = computed(() => groupsStore.groupIdForChannel(props.channelId) ?? groupsStore.selectedGroupId)
// While connecting (LiveKit has no participants yet), the stage shows a placeholder tile for each
// person already in the channel — from the channel's voice roster (`channel.users`, kept live over
// SignalR) plus us, unless the roster already lists us — with their avatar, which is already loaded:
// resolved through the on-disk media cache exactly like the real tiles (CallTile.vue), so it's the
// same cached file, not a new download.
const connectingPlaceholders = computed(() => {
  if (participants.value.length > 0) return []
  const groupId = callGroupId.value
  const channel = groupId ? groupsStore.findChannel(groupId, props.channelId) : null
  const people = (channel?.users ?? []).map(user => ({
    id: user.id,
    name: user.display_name || user.username || '',
    avatarUrl: resolveMediaUrl(user.avatar_url || groupsStore.getUserAvatarUrl(user.id), 'avatars'),
  }))
  const me = userStore.user
  if (me && !people.some(person => person.id === me.id)) {
    people.unshift({
      id: me.id,
      name: me.display_name || me.username || '',
      avatarUrl: resolveMediaUrl(me.avatar_url, 'avatars'),
    })
  }
  return people
})
const canSpeak = computed(() => !callGroupId.value || groupsStore.hasGroupPermission(callGroupId.value, PermissionFlag.Speak))
const canVideo = computed(() => !callGroupId.value || groupsStore.hasGroupPermission(callGroupId.value, PermissionFlag.Video))
// Only the group-role gate actually disables the buttons — an OS/browser permission denial isn't
// necessarily final (the user can grant it from a re-prompt, or from OS Settings without leaving
// the call), so those buttons stay clickable and just re-attempt `getUserMedia` on click, which
// re-prompts unless the OS has permanently blocked it. `isMicPermissionDenied`/
// `isCameraPermissionDenied` still drive the "no device access" modal/tooltip wording below.
const isMicBlocked = computed(() => !canSpeak.value)
const isCameraBlocked = computed(() => !canVideo.value)
const isScreenShareBlocked = computed(() => !canVideo.value)

async function recheckMediaPermissions() {
  await groupsStore.refreshMediaPermissions()
}

const RECONNECTING_TOAST_ID = 'voice-reconnecting'

watch(isReconnecting, (reconnecting) => {
  if (reconnecting) {
    toast.loading('Переподключение', {id: RECONNECTING_TOAST_ID, duration: Infinity, dismissible: false})
  } else {
    toast.dismiss(RECONNECTING_TOAST_ID)
  }
}, {immediate: true})

const emit = defineEmits<{
  (e: 'disconnect'): void
}>()

// Right-click layout toggles on the call background — persisted like every other display
// preference (see appSettings.ts's `callLayout`), read once on mount and patched on change.
const callLayoutSettings = loadAppSettings().callLayout
const viewMode = ref<CallViewMode>(callLayoutSettings.viewMode)
const showParticipantsWithoutVideo = ref(callLayoutSettings.showParticipantsWithoutVideo)

function setViewMode(value: CallViewMode | undefined) {
  // ToggleGroup type="single" emits '' instead of undefined when the active item is
  // clicked again — a single-select layout switcher must always keep one mode active.
  if (!value) return
  viewMode.value = value
  patchAppSettings({callLayout: {viewMode: value}})
}

function setShowParticipantsWithoutVideo(value: boolean) {
  showParticipantsWithoutVideo.value = value
  patchAppSettings({callLayout: {showParticipantsWithoutVideo: value}})
}

// Opening a video never touches the native window (see `enterFullscreen`): the DOM Fullscreen API
// silently no-ops in macOS WKWebView, Tauri's `setFullscreen(true)` switches to a separate Space that
// left this transparent-titlebar window's compositor stuck, and the maximize-without-decorations that
// replaced it took away the titlebar and filled the screen from a floating window. So this stays
// `false` — the video just covers the app window — and only gates the layout bits below.
const isFullscreen = ref(false)
const focusedVideo = ref<{ sid: string; source: Track.Source; name: string } | null>(null)
const focusedVideoEl = shallowRef<HTMLVideoElement | null>(null)

function resumeFocusedVideo() {
  focusedVideoEl.value?.play().catch(() => undefined)
}

// Mobile: the OS stops screen capture the moment the app is backgrounded (on iOS it's `replayd`
// itself — "stop capture for client capture full display entered background"), which ends the
// track and unpublishes the share. Nothing on screen says so, so on return we tell the user why
// their share is gone. Compared across the hide/show pair rather than reacting to the track's
// `ended` event: that event may only be processed after the app is already visible again.
let screenShareActiveWhenHidden = false
const SCREEN_SHARE_STOPPED_CHECK_DELAY_MS = 500

function handleVisibilityChange() {
  if (document.visibilityState === 'hidden') {
    screenShareActiveWhenHidden = isScreenShareEnabled.value
    return
  }
  if (focusedVideo.value) resumeFocusedVideo()
  if (isMobile.value && screenShareActiveWhenHidden) {
    screenShareActiveWhenHidden = false
    // Gives a queued `ended` → unpublish a moment to land before checking.
    setTimeout(() => {
      if (!isScreenShareEnabled.value) {
        toast.info('Демонстрация экрана остановлена', {
          description: 'Система не разрешает транслировать экран, пока приложение свёрнуто.',
        })
      }
    }, SCREEN_SHARE_STOPPED_CHECK_DELAY_MS)
  }
}

// ── Zoom the opened tile to full screen ──────────────────────────────────────
// The opened tile's video grows out of the tile itself to fill the screen on a black backdrop — a
// layer (`mobileZoomEl` / `desktopZoomEl`, see template) animated from the tile's box to the full
// view — and shrinks back into it on close. Mobile: a tap toggles the controls, a swipe down (or the
// button, or Android back) closes. Desktop: it fills the app window (not the screen); double-click,
// Esc or the button closes.
const mobileZoomEl = ref<HTMLElement | null>(null)
const desktopZoomEl = ref<HTMLElement | null>(null)
const zoomLayer = () => (isMobile.value ? mobileZoomEl.value : desktopZoomEl.value)
// The dimmed backdrop is its own full-size layer under the zoom layer, faded in and out on its own —
// as the zoom layer's background it grew out of the tile together with the video instead of simply
// fading in behind it.
const mobileZoomBackdropEl = ref<HTMLElement | null>(null)
const desktopZoomBackdropEl = ref<HTMLElement | null>(null)
const zoomBackdrop = () => (isMobile.value ? mobileZoomBackdropEl.value : desktopZoomBackdropEl.value)
const ZOOM_DURATION_MS = 350
const ZOOM_EASING = 'cubic-bezier(0.32, 0.72, 0, 1)'
let isZoomingOut = false

function findTileEl(info: { sid: string; source: Track.Source }) {
  return document.querySelector<HTMLElement>(
      `[data-call-tile-sid="${CSS.escape(info.sid)}"][data-call-tile-source="${CSS.escape(String(info.source))}"]`,
  )
}

// The zoom layer's geometry at the tile (inside the layer's own containing block) and full size.
// Top/left/width/height rather than a `transform: scale`: the video inside is `object-contain`, so
// resizing the box keeps it framed exactly like the tile at every step, where a non-uniform scale
// would squash it.
function tileFrame(tile: DOMRect, box: DOMRect): Keyframe {
  return {
    top: `${tile.top - box.top}px`,
    left: `${tile.left - box.left}px`,
    width: `${tile.width}px`,
    height: `${tile.height}px`,
    borderRadius: '12px',
    transform: 'none',
  }
}

// `100%`, not the box's pixel size: the layer ends at the full size of its container, whatever that
// is by then (a window resized mid-animation included).
function fullFrame(transform = 'none'): Keyframe {
  return {
    top: '0px',
    left: '0px',
    width: '100%',
    height: '100%',
    borderRadius: '0px',
    transform,
  }
}

async function playZoomIn() {
  await nextTick()
  const layer = zoomLayer()
  const tile = focusedVideo.value && findTileEl(focusedVideo.value)
  if (!layer || !tile) {
    fullscreenControlsVisible.value = true
    return
  }
  const box = layer.getBoundingClientRect()
  zoomBackdrop()?.animate([{opacity: 0}, {opacity: 1}], {duration: ZOOM_DURATION_MS, easing: 'ease-out'})
  const animation = layer.animate([tileFrame(tile.getBoundingClientRect(), box), fullFrame()], {
    duration: ZOOM_DURATION_MS,
    easing: ZOOM_EASING,
  })
  await animation.finished.catch(() => undefined)
  // Controls fade in once the video has landed (their own `transition-opacity`).
  if (focusedVideo.value) fullscreenControlsVisible.value = true
}

async function playZoomOut() {
  const layer = zoomLayer()
  const tile = focusedVideo.value && findTileEl(focusedVideo.value)
  if (!layer || !tile) return
  fullscreenControlsVisible.value = false
  // The layer's resting box (= its containing block, it's `inset-0`), read with any swipe-down
  // transform lifted for the read — then the animation starts from wherever that swipe left it.
  const dragTransform = layer.style.transform
  layer.style.transform = ''
  const box = layer.getBoundingClientRect()
  layer.style.transform = dragTransform
  const backdrop = zoomBackdrop()
  // From wherever a swipe-down had faded it to.
  const backdropOpacity = backdrop ? getComputedStyle(backdrop).opacity : '1'
  backdrop?.animate([{opacity: backdropOpacity}, {opacity: 0}], {duration: ZOOM_DURATION_MS, easing: 'ease-in', fill: 'forwards'})
  const animation = layer.animate([fullFrame(dragTransform || 'none'), tileFrame(tile.getBoundingClientRect(), box)], {
    duration: ZOOM_DURATION_MS,
    easing: ZOOM_EASING,
    fill: 'forwards',
  })
  await animation.finished.catch(() => undefined)
}

// Swipe down to close: the layer follows the finger (and the black backdrop fades) and, released
// past DISMISS_DISTANCE_PX or with a quick flick, shrinks back into the tile; otherwise springs back.
// Pointer events stop here, so the call view's own Drawer (MobileCallOverlay.vue) doesn't also get
// dragged down underneath.
const DISMISS_DISTANCE_PX = 120
const DISMISS_VELOCITY_PX_PER_MS = 0.5
let dragStart: { y: number; t: number; pointerId: number } | null = null
let dragDy = 0
let dragMoved = false

function handleZoomPointerDown(event: PointerEvent) {
  event.stopPropagation()
  if (isZoomingOut) return
  dragStart = {y: event.clientY, t: event.timeStamp, pointerId: event.pointerId}
  dragDy = 0
  dragMoved = false
}

function handleZoomPointerMove(event: PointerEvent) {
  event.stopPropagation()
  const layer = mobileZoomEl.value
  if (!dragStart || event.pointerId !== dragStart.pointerId || !layer) return
  dragDy = Math.max(0, event.clientY - dragStart.y)
  if (dragDy > 8) dragMoved = true
  if (!dragMoved) return
  const scale = Math.max(0.8, 1 - dragDy / 2000)
  layer.style.transform = `translateY(${dragDy}px) scale(${scale})`
  const backdrop = mobileZoomBackdropEl.value
  if (backdrop) backdrop.style.opacity = String(Math.max(0, 1 - dragDy / 400))
}

function handleZoomPointerEnd(event: PointerEvent) {
  event.stopPropagation()
  const layer = mobileZoomEl.value
  if (!dragStart || !layer) return
  const velocity = dragDy / Math.max(1, event.timeStamp - dragStart.t)
  const shouldDismiss = dragMoved && event.type === 'pointerup'
      && (dragDy > DISMISS_DISTANCE_PX || velocity > DISMISS_VELOCITY_PX_PER_MS)
  dragStart = null
  if (!dragMoved) return
  if (shouldDismiss) {
    void exitFullscreen()
    return
  }
  const current = layer.style.transform
  layer.style.transform = ''
  layer.animate([{transform: current}, {transform: 'none'}], {duration: 250, easing: ZOOM_EASING})
  const backdrop = mobileZoomBackdropEl.value
  if (backdrop) {
    const opacity = backdrop.style.opacity
    backdrop.style.opacity = ''
    backdrop.animate([{opacity}, {opacity: 1}], {duration: 250, easing: ZOOM_EASING})
  }
}

// Whether a click landed on the dimmed area around the picture rather than on the picture itself.
// The <video> fills the whole layer with `object-contain`, so the picture's own box is worked out
// from the stream's size. Before the stream's size is known, everything counts as the picture.
function isOutsideFocusedPicture(event: MouseEvent) {
  const video = focusedVideoEl.value
  if (!video || !video.videoWidth || !video.videoHeight) return false
  const box = video.getBoundingClientRect()
  const scale = Math.min(box.width / video.videoWidth, box.height / video.videoHeight)
  const width = video.videoWidth * scale
  const height = video.videoHeight * scale
  const left = box.left + (box.width - width) / 2
  const top = box.top + (box.height - height) / 2
  return event.clientX < left || event.clientX > left + width || event.clientY < top || event.clientY > top + height
}

// A drag ends with a `click` on some WebViews — only a real tap counts. A tap on the picture toggles
// the controls; a tap outside it closes, same as on desktop.
function handleZoomClick(event: MouseEvent) {
  if (dragMoved) {
    dragMoved = false
    return
  }
  if (isOutsideFocusedPicture(event)) {
    void exitFullscreen()
    return
  }
  toggleFullscreenControls()
}

// Desktop: a single click outside the picture closes (double-click anywhere still does too).
function handleDesktopZoomClick(event: MouseEvent) {
  if (isOutsideFocusedPicture(event)) void exitFullscreen()
}

async function enterFullscreen(sid: string, source: Track.Source, name: string) {
  focusedVideo.value = {sid, source, name}
  // Shown once the zoom has landed (see `playZoomIn`).
  fullscreenControlsVisible.value = false

  if (isMobile.value) {
    await playZoomIn()
    resumeFocusedVideo()
    return
  }

  // Desktop: the video fills the app window it's in — the window itself is left alone. It used to be
  // native-maximized with its decorations dropped, which from a floating window took away the
  // titlebar (and its close/minimize buttons) and blew the window up to the whole screen.
  await playZoomIn()
  resumeFocusedVideo()
}

async function exitFullscreen() {
  // Shrinks back into the tile first.
  if (focusedVideo.value) {
    if (isZoomingOut) return
    isZoomingOut = true
    try {
      await playZoomOut()
    } finally {
      isZoomingOut = false
    }
  }

  // Snapshot before clearing: once focusedVideo is null the video's :ref
  // callback can no longer read sid/source off it (that's what used to throw
  // mid-unmount and leave the overlay stuck in the DOM), so unregister it here
  // ourselves using the values captured while they're still available.
  const info = focusedVideo.value
  const el = focusedVideoEl.value
  if (info && el) {
    const pMap = videoElements.get(info.sid)
    const set = pMap?.get(info.source)
    if (set) {
      releaseVideoElement(el, info.sid, info.source)
      set.delete(el)
      if (set.size === 0) pMap?.delete(info.source)
    }
    if (pMap?.size === 0) videoElements.delete(info.sid)
  }

  focusedVideo.value = null
  focusedVideoEl.value = null
}

// Closes the opened video on its own once there's nothing left to show — the participant stopped
// that screen share or turned that camera off, left the call, or (someone else's stream) it's no
// longer being watched. Otherwise the viewer stayed up on a frozen/blank frame with nothing to exit.
const focusedVideoGone = computed(() => {
  const info = focusedVideo.value
  if (!info) return false
  const participant = participants.value.find(p => p.sid === info.sid)
  if (!participant) return true
  if (info.source === Track.Source.ScreenShare) {
    return !participant.isScreenShareEnabled
      || (!participant.is_local && !groupsStore.isScreenShareWatched(participant.identity))
  }
  if (info.source === Track.Source.Camera) return !participant.isVideoEnabled
  return false
})
watch(focusedVideoGone, (gone) => {
  if (gone) void exitFullscreen()
})

const hotkeys = useHotkeys()
function handleFullscreenKeydown(event: KeyboardEvent) {
  if (hotkeys.matches('common.cancel', event) && focusedVideo.value) {
    event.preventDefault()
    exitFullscreen()
  }
}

// Tap-to-hide chrome inside the fullscreen tile (minimize button/name label on desktop, plus the
// duplicated `CallControls` on mobile) — desktop keeps its own hover-driven visibility (see the
// template's `!isMobile ||` guards), so this ref only ever actually changes on mobile.
const fullscreenControlsVisible = ref(true)

function toggleFullscreenControls() {
  fullscreenControlsVisible.value = !fullscreenControlsVisible.value
}

// Lets the Android hardware/gesture back button close the mobile full-screen video (see
// useMobileDrawerBackNav.ts) — closing goes through the same `exitFullscreen` as the button.
const mobileZoomOpen = computed({
  get: () => !!focusedVideo.value,
  set: (open: boolean) => {
    if (!open) void exitFullscreen()
  },
})
useMobileDrawerCloseOnBack({isOpen: mobileZoomOpen, enabled: isMobile})

// On macOS, an unclaimed Esc is consumed by AppKit's `-cancelOperation:` key-equivalent
// path before WKWebView ever turns it into a DOM `keydown` — so the listener above alone
// never fires there. The Rust side (see lib.rs's NSEvent local monitor) emits this Tauri
// event for that exact case, letting Esc still close this (in-window)
// fullscreen view. Harmless no-op on other platforms, where `handleFullscreenKeydown` above
// already does the job and this event is simply never emitted.
let unlistenEscape: UnlistenFn | null = null


// Selection modals
const cameraModalOpen = ref(false)
const screenShareModalOpen = ref(false)
const pendingCamera = ref("")

// Debug panel
type DebugEvent = { ts: number, source: string, level: string, message: string }
const debugPanelOpen = ref(false)
const debugEvents = ref<DebugEvent[]>([])
const MAX_DEBUG_EVENTS = 200

function addDebug(ev: DebugEvent) {
  debugEvents.value.unshift(ev)
  if (debugEvents.value.length > MAX_DEBUG_EVENTS) {
    debugEvents.value.length = MAX_DEBUG_EVENTS
  }
}

function frontendDebug(source: string, level: string, message: string) {
  addDebug({ts: Date.now(), source, level, message})
  console.log(`[dbg:${source}:${level}] ${message}`)
}

const videoElements = new Map<string, Map<Track.Source, Set<HTMLVideoElement>>>()

function updateTrackAttachments() {
  const room = liveKitManager.room
  if (!room) return

  frontendDebug('livekit', 'info', 'Updating all track attachments')
  const all = [room.localParticipant, ...Array.from(room.remoteParticipants.values())]
  all.forEach(p => {
    p.trackPublications.forEach(pub => {
      if (pub.track && (pub.isSubscribed || p.isLocal)) {
        attachTrack(pub.track, p.sid)
      }
    })
  })
}

const onTrackSubscribed = (track: any, pub: any, p: any) => {
  frontendDebug('livekit', 'info', `Track subscribed: ${track.kind} from ${p.identity}`)
  attachTrack(track, p.sid)
}

const onLocalTrackPublished = (pub: any) => {
  if (pub.track) {
    frontendDebug('livekit', 'info', `Local track published: ${pub.track.kind}`)
    attachTrack(pub.track, liveKitManager.room!.localParticipant.sid)
  }
}

watch(activeRoom, (newRoom, oldRoom) => {
  if (oldRoom) {
    oldRoom.off(RoomEvent.TrackSubscribed, onTrackSubscribed)
    oldRoom.off(RoomEvent.LocalTrackPublished, onLocalTrackPublished)
  }
  if (newRoom) {
    newRoom.on(RoomEvent.TrackSubscribed, onTrackSubscribed)
    newRoom.on(RoomEvent.LocalTrackPublished, onLocalTrackPublished)
    updateTrackAttachments()
  }
}, {immediate: true})

onMounted(async () => {
  frontendDebug('frontend', 'info', 'VoiceCallView mounted')

  if (groupsStore.activeVoiceChannelId !== props.channelId) {
    try {
      await groupsStore.connectToVoice(props.channelId)
    } catch (e: any) {
      toast.error(`Не удалось подключиться к каналу: ${e?.message || String(e)}`)
      emit('disconnect')
      return
    }
  }

  updateTrackAttachments()

  window.addEventListener('keydown', handleFullscreenKeydown)
  document.addEventListener('visibilitychange', handleVisibilityChange)
  // 'app-escape-pressed' is a Tauri-only global-shortcut event (see architecture doc) — browser
  // mode already gets Esc-to-exit-fullscreen from `handleFullscreenKeydown` above via the plain
  // `keydown` listener.
  if (isTauriRuntime()) {
    unlistenEscape = await listen('app-escape-pressed', () => {
      if (focusedVideo.value) exitFullscreen()
    })
  }
})

// The share can end without going through `toggleScreenShare` (call left, connection dropped,
// LiveKit unpublishing it) — whichever way, the iOS broadcast must not outlive it.
watch(isScreenShareEnabled, (enabled) => {
  if (!enabled && isIosScreenBroadcastActive()) void stopIosScreenBroadcast()
})

onUnmounted(() => {
  if (isIosScreenBroadcastActive()) void stopIosScreenBroadcast()
  toast.dismiss(RECONNECTING_TOAST_ID)
  window.removeEventListener('keydown', handleFullscreenKeydown)
  document.removeEventListener('visibilitychange', handleVisibilityChange)
  unlistenEscape?.()
  unlistenEscape = null
  if (isFullscreen.value) void exitFullscreen()
  if (activeRoom.value) {
    activeRoom.value.off(RoomEvent.TrackSubscribed, onTrackSubscribed)
    activeRoom.value.off(RoomEvent.LocalTrackPublished, onLocalTrackPublished)
  }
  for (const [sid, sources] of videoElements) {
    for (const [source, elements] of sources) {
      for (const element of elements) releaseVideoElement(element, sid, source)
    }
  }
  videoElements.clear()
})

// Takes a <video> off its track: through LiveKit's own `detach`, so the track stops counting it as
// attached (adaptive stream decides whether to keep receiving video from what's still attached), and
// then its stream cleared.
// The room participant with this `sid`. Not `room.remoteParticipants.get(sid)`: since
// livekit-client v2 that map is keyed by *identity*, so a lookup by sid always came back empty — a
// <video> mounted after its track had been subscribed (a re-created tile) never got the track
// attached, and only ever showed a picture when the stream happened to be copied over from another
// element.
function findParticipantBySid(sid: string) {
  const room = liveKitManager.room
  if (!room) return undefined
  if (room.localParticipant.sid === sid) return room.localParticipant
  for (const participant of room.remoteParticipants.values()) {
    if (participant.sid === sid) return participant
  }
  return undefined
}

function releaseVideoElement(el: HTMLVideoElement, sid: string, source: Track.Source) {
  const p = findParticipantBySid(sid)
  p?.trackPublications.forEach(pub => {
    if (pub.track && pub.source === source) pub.track.detach(el)
  })
  el.srcObject = null
}

function pruneDisconnectedVideoElements(sid: string, source: Track.Source) {
  const sources = videoElements.get(sid)
  const elements = sources?.get(source)
  if (elements) {
    for (const element of elements) {
      if (!element.isConnected) {
        releaseVideoElement(element, sid, source)
        elements.delete(element)
      }
    }
    if (elements.size === 0) sources?.delete(source)
  }
  if (sources?.size === 0) videoElements.delete(sid)
}

function setVideoRef(el: HTMLVideoElement | null, sid: string, source: Track.Source) {
  if (!el) {
    // Vue calls a function ref with `null` *before* it takes the element out of the DOM, so an
    // `isConnected` check right here saw every unmounting <video> as still attached and none was
    // ever released: they piled up in the set ("Total for this source: 2" on a camera turned back
    // on) and stayed attached to the LiveKit track. Checked once the unmount has finished instead.
    queueMicrotask(() => pruneDisconnectedVideoElements(sid, source))
    return
  }

  // One <video> shows one track: take it out of every other (participant, source) set first. The
  // same element does get re-registered under a new source while staying mounted — a participant's
  // tile is keyed by their sid, so starting a screen share turns their camera tile into the screen
  // tile in place (and the PiP swap re-points it too) — and the `null` unregister for the old source
  // can't drop it then, since it's still in the DOM. Left in the camera set, the screen tile's
  // <video> got the camera track attached over the screen share the next time the camera came on,
  // so both tiles showed the camera.
  for (const [otherSid, sources] of videoElements) {
    for (const [otherSource, elements] of sources) {
      if ((otherSid !== sid || otherSource !== source) && elements.delete(el)) {
        releaseVideoElement(el, otherSid, otherSource)
        if (elements.size === 0) sources.delete(otherSource)
      }
    }
    if (sources.size === 0) videoElements.delete(otherSid)
  }

  let pMap = videoElements.get(sid)
  if (!pMap) {
    pMap = new Map()
    videoElements.set(sid, pMap)
  }

  let set = pMap.get(source)
  if (!set) {
    set = new Set()
    pMap.set(source, set)
  }

  if (set.has(el)) return
  set.add(el)
  frontendDebug('ui', 'info', `Registered video element for ${sid} source ${source}. Total for this source: ${set.size}`)
  const p = findParticipantBySid(sid)
  if (!p) frontendDebug('video', 'warn', `No participant ${sid} in the room for a new ${source} element`)
  p?.trackPublications.forEach(pub => {
    if (pub.track && pub.kind === Track.Kind.Video && pub.source === source) {
      frontendDebug('video', 'info', `Attaching existing track for ${sid} to new element`)
      pub.track.attach(el)
    }
  })

  // Defensive fallback: if the publication lookup above didn't find anything
  // (e.g. the room reference is momentarily stale), mirror the stream from
  // another element already showing this same track — this is what the
  // fullscreen overlay's freshly-mounted <video> relies on in practice.
  // Always into a *new* MediaStream with only the live video tracks — never the other element's
  // `srcObject` itself: LiveKit's `attach` reuses whatever MediaStream an element already holds and
  // its `detach` removes the track from it, so two elements sharing one stream meant detaching one
  // (or attaching the newly subscribed track to it) pulled the video out from under the other —
  // the tile went "starting" with no picture. Also skips a copy of an ended track from a stale one.
  if (!el.srcObject) {
    for (const existing of set) {
      if (existing === el || !(existing.srcObject instanceof MediaStream)) continue
      const liveTracks = existing.srcObject.getVideoTracks().filter(t => t.readyState === 'live')
      if (liveTracks.length) {
        el.srcObject = new MediaStream(liveTracks)
        break
      }
    }
  }
  void el.play().catch(() => undefined)
}

function attachTrack(track: any, sid: string) {
  nextTick(() => {
    if (track.kind === Track.Kind.Video) {
      const pMap = videoElements.get(sid)
      if (pMap) {
        const elements = pMap.get(track.source)
        if (elements) {
          frontendDebug('video', 'info', `Attaching ${track.source} track for ${sid} to ${elements.size} elements`)
          elements.forEach(el => track.attach(el))
        } else {
          frontendDebug('video', 'warn', `No elements found for ${track.source} track of ${sid}`)
        }
      } else {
        frontendDebug('video', 'warn', `No element map found for participant ${sid}`)
      }
    }
  })
}

// Actions
const changeMicrophone = async (deviceId: string) => {
  try {
    await liveKitManager.changeMicrophone(deviceId)
    frontendDebug('mic', 'info', `Microphone switched to: ${deviceId}`)
  } catch (error) {
    frontendDebug('mic', 'error', `Failed to switch microphone: ${error}`)
    Sentry.captureException(error, {extra: {action: 'switch_microphone', deviceId}})
    toast.error('Не удалось переключить микрофон')
  }
}

const changeCamera = async (deviceId: string) => {
  try {
    await liveKitManager.changeCamera(deviceId)
    frontendDebug('camera', 'info', `Camera switched to: ${deviceId}`)
  } catch (error) {
    frontendDebug('camera', 'error', `Failed to switch camera: ${error}`)
    Sentry.captureException(error, {extra: {action: 'switch_camera', deviceId}})
    toast.error('Не удалось переключить камеру')
  }
}

const changeSpeaker = async (deviceId: string) => {
  try {
    await liveKitManager.changeSpeaker(deviceId)
    frontendDebug('speaker', 'info', `Speaker switched to: ${deviceId}`)
  } catch (error) {
    frontendDebug('speaker', 'error', `Failed to switch speaker: ${error}`)
    Sentry.captureException(error, {extra: {action: 'switch_speaker', deviceId}})
    toast.error('Не удалось переключить динамик')
  }
}

const toggleMute = async () => {
  try {
    await groupsStore.toggleMic()
    frontendDebug('mic', 'info', `Microphone toggled`)
  } catch (error) {
    frontendDebug('mic', 'error', `Failed to toggle microphone: ${error}`)
    Sentry.captureException(error, {extra: {action: 'toggle_microphone'}})
    toast.error('Не удалось включить микрофон — проверьте разрешения')
  }
}

const toggleDeaf = async () => {
  try {
    await groupsStore.toggleDeaf()
    frontendDebug('mic', 'info', `Deafen toggled`)
  } catch (error) {
    frontendDebug('mic', 'error', `Failed to toggle deafen: ${error}`)
    Sentry.captureException(error, {extra: {action: 'toggle_deafen'}})
  }
}

const toggleNoiseSuppression = async () => {
  try {
    await groupsStore.toggleNoiseSuppression()
    const status = isNoiseSuppressionEnabled.value ? 'включено' : 'выключено'
    frontendDebug('mic', 'info', `Noise suppression ${status}`)
    toast.info(`AggressiveNoiseProcessor ${status}`, {duration: 2000})
  } catch (error) {
    frontendDebug('mic', 'error', `Failed to toggle noise suppression: ${error}`)
    Sentry.captureException(error, {extra: {action: 'toggle_noise_suppression'}})
    toast.error('Не удалось переключить шумоподавление')
  }
}

const toggleEchoCancellation = async () => {
  try {
    await groupsStore.toggleEchoCancellation()
    const status = isEchoCancellationEnabled.value ? 'включено' : 'выключено'
    frontendDebug('mic', 'info', `Echo cancellation ${status}`)
    toast.info(`Эхоподавление ${status}`, {duration: 2000})
  } catch (error) {
    frontendDebug('mic', 'error', `Failed to toggle echo cancellation: ${error}`)
    Sentry.captureException(error, {extra: {action: 'toggle_echo_cancellation'}})
    toast.error('Не удалось переключить эхоподавление')
  }
}

const setSelfMonitoring = async (enabled: boolean) => {
  try {
    await groupsStore.setSelfMonitoring(enabled)
    frontendDebug('mic', 'info', `Self-monitoring ${enabled ? 'включено' : 'выключено'}`)
  } catch (error) {
    frontendDebug('mic', 'error', `Failed to toggle self-monitoring: ${error}`)
    Sentry.captureException(error, {extra: {action: 'toggle_self_monitoring'}})
    toast.error(error instanceof Error ? error.message : 'Не удалось включить прослушивание себя')
  }
}

const toggleVideo = async () => {
  if (isCameraBlocked.value) return
  try {
    await groupsStore.toggleCamera()
    frontendDebug('camera', 'info', `Camera toggled`)
  } catch (error) {
    frontendDebug('camera', 'error', `Failed to toggle camera: ${error}`)
    Sentry.captureException(error, {extra: {action: 'toggle_camera'}})
    toast.error('Не удалось включить камеру — проверьте разрешения')
  }
}

const openCameraModal = async () => {
  await liveKitManager.refreshDevices()
  pendingCamera.value = liveKitManager.state.selectedCameraId || liveKitManager.state.cameras[0]?.deviceId || ""
  cameraModalOpen.value = true
}

const confirmCamera = async () => {
  cameraModalOpen.value = false
  if (!pendingCamera.value) return
  await changeCamera(pendingCamera.value)
  if (!isCameraEnabled.value) {
    await groupsStore.toggleCamera()
  }
}

// Mobile has no source picker of our own — `ScreenShareModal` is built entirely around the
// desktop-only Rust capture commands (`get_capture_sources`/`start_live_capture`, both `xcap`
// under the hood, which doesn't even build for iOS). Calling `getDisplayMedia()` directly instead
// hands the whole thing to the WebView: it shows the OS's own native picker (ReplayKit's
// broadcast prompt on iOS, MediaProjection's permission dialog on Android) and hands back a
// ready-to-publish `MediaStream`, with zero native/Rust code on our side. Unverified whether
// Tauri's mobile WebViews actually support this API end to end — if a device doesn't, this
// throws and we surface that below instead of silently doing nothing.
async function startMobileScreenShare() {
  // iOS: a ReplayKit broadcast instead, the only capture that survives the app being backgrounded
  // (see lib/livekit/iosScreenBroadcast.ts). No app audio: the broadcast carries video only.
  if (isIosScreenBroadcastAvailable()) {
    try {
      const stream = await startIosScreenBroadcast(() => {
        // Stopped from outside the app (status-bar pill / Control Center): unpublish ours too.
        if (isScreenShareEnabled.value) void groupsStore.toggleScreenShare()
      })
      await startScreenShare(stream, screenShareQuality.value, screenShareFps.value, false)
    } catch (error) {
      if (error instanceof IosBroadcastCancelledError) return
      frontendDebug('screen', 'error', `iOS screen broadcast failed: ${error}`)
      Sentry.captureException(error, {extra: {action: 'ios_screen_broadcast'}})
      toast.error('Не удалось начать демонстрацию экрана')
    }
    return
  }
  try {
    const stream = await navigator.mediaDevices.getDisplayMedia({video: true, audio: true})
    await startScreenShare(stream, screenShareQuality.value, screenShareFps.value, true)
  } catch (error) {
    // The user dismissing the native picker also throws `NotAllowedError` — that's a normal
    // cancellation, not a failure worth surfacing.
    if (error instanceof DOMException && error.name === 'NotAllowedError') return
    frontendDebug('screen', 'error', `Mobile getDisplayMedia failed: ${error}`)
    Sentry.captureException(error, {extra: {action: 'mobile_get_display_media'}})
    toast.error('Демонстрация экрана недоступна на этом устройстве')
  }
}

const toggleScreenShare = async () => {
  if (isScreenShareBlocked.value) return
  if (!isScreenShareEnabled.value) {
    if (isMobile.value) {
      await startMobileScreenShare()
      return
    }
    screenShareModalOpen.value = true
    return
  }

  // iOS: end the broadcast right away rather than after LiveKit has unpublished the track (the
  // `isScreenShareEnabled` watcher above only fires once that server round trip is done, which kept
  // the red status-bar pill and the capture itself going noticeably after the tap).
  if (isIosScreenBroadcastActive()) void stopIosScreenBroadcast()

  try {
    await groupsStore.toggleScreenShare()
    frontendDebug('screen', 'info', `Screen share toggled`)
  } catch (error) {
    frontendDebug('screen', 'error', `Failed to toggle screen share: ${error}`)
    Sentry.captureException(error, {extra: {action: 'toggle_screen_share'}})
  }
}

const startScreenShare = async (stream: MediaStream, quality: any, fps: number, includeAudio: boolean) => {
  try {
    await groupsStore.startScreenShare(stream, quality, fps, includeAudio)
    screenShareModalOpen.value = false
    frontendDebug('screen', 'info', `Screen share started (${quality}, ${fps} FPS)`)
  } catch (error) {
    stream.getTracks().forEach(track => track.stop())
    frontendDebug('screen', 'error', `Failed to start screen share: ${error}`)
    Sentry.captureException(error, {extra: {action: 'start_screen_share_livekit'}})
    toast.error('Не удалось начать демонстрацию')
  }
}

const changeScreenSharePreset = async (preset: 'text' | 'motion') => {
  await groupsStore.setScreenSharePreset(preset)
}

function clearDebug() {
  debugEvents.value = []
}

// Shared with the mobile fullscreen-tile overlay below (see `focusedVideo`) — it renders a
// second `CallControls` instance on top of the fullscreen video (that overlay sits above the
// normal one, z-index-wise, so the original panel is literally hidden behind it), and re-typing
// ~30 prop/emit bindings for a second copy would just drift out of sync with this one over time.
const callControlsProps = computed(() => ({
  isMuted: isMicMuted.value,
  isDeafened: isDeafened.value,
  isVideoOn: isCameraEnabled.value,
  isScreenSharing: isScreenShareEnabled.value,
  microphones: liveKitManager.state.microphones,
  speakers: liveKitManager.state.speakers,
  cameras: liveKitManager.state.cameras,
  selectedMic: liveKitManager.state.selectedMicId,
  selectedSpeaker: liveKitManager.state.selectedSpeakerId,
  selectedCamera: liveKitManager.state.selectedCameraId,
  isNoiseSuppressionEnabled: isNoiseSuppressionEnabled.value,
  isNoiseSuppressionPending: isNoiseSuppressionPending.value,
  isEchoCancellationEnabled: isEchoCancellationEnabled.value,
  isSelfMonitoring: isSelfMonitoring.value,
  micVolume: micVolume.value,
  speakerVolume: speakerVolume.value,
  micSensitivity: micSensitivity.value,
  videoQuality: videoQuality.value,
  screenShareQuality: screenShareQuality.value,
  screenShareFps: screenShareFps.value,
  isScreenShareAudioEnabled: isScreenShareAudioEnabled.value,
  micBlocked: isMicBlocked.value,
  cameraBlocked: isCameraBlocked.value,
  screenShareBlocked: isScreenShareBlocked.value,
}))

const callControlsHandlers = {
  toggleMute,
  toggleDeaf,
  toggleVideo,
  toggleScreenShare,
  toggleNoiseSuppression,
  toggleEchoCancellation,
  setSelfMonitoring,
  disconnect: () => emit('disconnect'),
  openCameraModal,
  changeMic: changeMicrophone,
  changeSpeaker,
  changeCamera,
  changeMicVolume: (v: number) => groupsStore.setMicVolume(v),
  changeSpeakerVolume: (v: number) => groupsStore.setSpeakerVolume(v),
  changeMicSensitivity: (v: number) => groupsStore.setMicSensitivity(v),
  changeVideoQuality: (q: any) => groupsStore.setVideoQuality(q),
  changeScreenShareQuality: (q: any) => groupsStore.setScreenShareQuality(q),
  changeScreenShareFps: (fps: number) => groupsStore.setScreenShareFps(fps),
  changeScreenSharePreset,
  changeScreenShareAudio: (enabled: boolean) => groupsStore.setScreenShareAudioEnabled(enabled),
}

// Mobile-only: a single tap on empty stage space (not on a tile — those `@click.stop` their own
// tap-to-expand, see `CallTile.vue`) hides the header + controls; the next tap brings them back.
// Doesn't apply while an `enterFullscreen()`-maximized video is up — that already has its own
// hover-to-reveal controls, this is for the normal call screen.
const controlsVisible = ref(true)

function handleStageClick() {
  if (!isMobile.value || isFullscreen.value) return
  controlsVisible.value = !controlsVisible.value
}

</script>

<template>
  <ContextMenu>
    <ContextMenuTrigger as-child>
      <div class="flex flex-col h-full bg-background relative overflow-hidden">

        <VoiceBackground v-if="!isMobile"/>
        <!-- Collapsed via `max-height`/`opacity` on an always-mounted wrapper, not `v-if` — an
             instant unmount also instantly frees this flex column's space, which snaps `CallStage`'s
             `flex-1` area (and its vertical centering, see `CallStage.vue`) to its new size in one
             frame. Animating the wrapper's own height lets that reflow happen gradually instead. -->
        <div
            v-if="!isFullscreen"
            class="shrink-0 overflow-hidden transition-[max-height,opacity] duration-300 ease-in-out"
            :class="(!isMobile || controlsVisible) ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'"
        >
          <CallHeader
              :group-name="groupName"
              :channelName="channelName"
              :roomState="roomState"
              :debugPanelOpen="debugPanelOpen"
              @toggleDebug="debugPanelOpen = !debugPanelOpen"
          />
        </div>

        <!-- Desktop: teleported to `body` (own window, no enclosing Drawer to fight with); covers the
             app window, the native window itself is left alone (see `enterFullscreen`). -->
        <!-- A separate backdrop dims the call behind (`bg-black/80`) rather than hiding it, and fades in
             on its own while the video layer above grows out of the tile (see `playZoomIn`) — same as
             mobile's. Both are animated with `animate()`, so no <Transition> here. -->
        <Teleport v-if="!isMobile" to="body">
            <div
                v-if="focusedVideo"
                ref="desktopZoomBackdropEl"
                class="fixed inset-0 z-80 bg-black/80"
            />
            <div
                v-if="focusedVideo"
                ref="desktopZoomEl"
                class="fixed inset-0 z-80 flex items-center justify-center overflow-hidden cursor-pointer"
                @click="handleDesktopZoomClick"
                @dblclick="exitFullscreen()"
            >
              <video
                  :key="`${focusedVideo.sid}-${focusedVideo.source}`"
                  :ref="el => { if (el && focusedVideo) { focusedVideoEl = el as HTMLVideoElement; setVideoRef(el as HTMLVideoElement, focusedVideo.sid, focusedVideo.source) } }"
                  class="w-full h-full object-contain pointer-events-none"
                  :class="focusedVideo.source === Track.Source.Camera ? '-scale-x-100' : ''"
                  autoplay
                  playsinline
              />
              <button
                  class="absolute cursor-pointer p-2 bg-black/40 z-100 hover:bg-black/60 backdrop-blur-md rounded-full text-white transition-opacity duration-200"
                  :class="fullscreenControlsVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'"
                  style="top: calc(env(safe-area-inset-top) + 2rem); right: calc(env(safe-area-inset-right) + 2rem)"
                  @click.stop="exitFullscreen"
              >
                <Minimize2 class="w-5 h-5"/>
              </button>
              <div
                  class="absolute px-3 py-1 bg-black/60 backdrop-blur-md rounded-full text-xs text-white transition-opacity duration-200"
                  :class="fullscreenControlsVisible ? 'opacity-100' : 'opacity-0'"
                  style="bottom: calc(env(safe-area-inset-bottom) + 1rem); left: calc(env(safe-area-inset-left) + 1rem)"
              >
                {{ focusedVideo.name }}
              </div>
            </div>
        </Teleport>

        <!-- Mobile: the tapped tile's video zoomed to full screen in place (see `playZoomIn` /
             `playZoomOut`) rather than a separate sheet. `fixed inset-0`: the call view renders inside
             its own Drawer (MobileCallOverlay.vue), whose transform makes that Drawer — the whole
             screen — this layer's containing block. `data-vaul-no-drag` + the handlers stopping
             pointer events keep a swipe-down here from dragging that Drawer too. -->
        <div
            v-if="isMobile && focusedVideo"
            ref="mobileZoomBackdropEl"
            class="fixed inset-0 z-50 bg-black"
        />
        <div
            v-if="isMobile && focusedVideo"
            ref="mobileZoomEl"
            data-vaul-no-drag
            class="fixed inset-0 z-50 flex touch-none items-center justify-center overflow-hidden"
            @pointerdown="handleZoomPointerDown"
            @pointermove="handleZoomPointerMove"
            @pointerup="handleZoomPointerEnd"
            @pointercancel="handleZoomPointerEnd"
            @click="handleZoomClick"
        >
            <div class="relative flex h-full w-full items-center justify-center">
              <!-- Not mirrored on mobile, same as the call tiles (CallTile.vue). -->
              <video
                  :key="`${focusedVideo.sid}-${focusedVideo.source}`"
                  :ref="el => { if (el && focusedVideo) { focusedVideoEl = el as HTMLVideoElement; setVideoRef(el as HTMLVideoElement, focusedVideo.sid, focusedVideo.source) } }"
                  class="w-full h-full object-contain pointer-events-none"
                  autoplay
                  playsinline
              />
              <Button
                  class="absolute rounded-full transition-opacity duration-200"
                  size="icon"
                  variant="secondary"
                  :class="fullscreenControlsVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'"
                  style="top: calc(env(safe-area-inset-top) + 2rem); right: calc(env(safe-area-inset-right) + 1rem)"
                  @pointerdown.stop
                  @click.stop="exitFullscreen"
              >
                <ChevronDown class="w-5 h-5"/>
              </Button>
              <Badge
                  variant="secondary"
                  class="absolute h-9 px-4 transition-opacity duration-200"
                  :class="fullscreenControlsVisible ? 'opacity-100' : 'opacity-0'"
                  style="top: calc(env(safe-area-inset-top) + 2rem); left: calc(env(safe-area-inset-left) + 1rem)"
              >
                {{ focusedVideo.name }}
              </Badge>
              <!-- The normal (non-fullscreen) `CallControls` further down is still mounted, but it's
                   `z-30` and this layer sits above it — a second instance, sharing the exact same
                   props/handlers, is the only way to actually show it on top. -->
              <!-- `bottom`, not `padding-bottom`: `CallControls`' pill is itself `absolute bottom-4`
                   against this box, and absolute children ignore their container's padding — so
                   the inset has to lift the box itself. -->
              <div
                  class="absolute inset-x-0 transition-opacity duration-200"
                  :class="fullscreenControlsVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'"
                  style="bottom: env(safe-area-inset-bottom)"
                  @pointerdown.stop
                  @click.stop
              >
                <CallControls v-bind="callControlsProps" v-on="callControlsHandlers"/>
              </div>
            </div>
        </div>

        <div class="flex-1 flex overflow-hidden flex-col relative z-[5] h-full" @click="handleStageClick">
          <CallStage
              :participants="participants"
              :view-mode="viewMode"
              :show-participants-without-video="showParticipantsWithoutVideo"
              :placeholders="connectingPlaceholders"
              @register-video="setVideoRef"
              @request-fullscreen="enterFullscreen"
              @show-all-participants="setShowParticipantsWithoutVideo(true)"
          />

          <!--      <DebugPanel-->
          <!--        v-if="debugPanelOpen && !isFullscreen"-->
          <!--        class="z-[110]"-->
          <!--        :events="debugEvents"-->
          <!--        :isMuted="isMicMuted"-->
          <!--        :isVideoOn="isCameraEnabled"-->
          <!--        :isScreenSharing="isScreenShareEnabled"-->
          <!--        :selectedMic="liveKitManager.state.selectedMicId"-->
          <!--        :selectedCamera="liveKitManager.state.selectedCameraId"-->
          <!--        @clear="clearDebug"-->
          <!--        @close="debugPanelOpen = false"-->
          <!--      />-->
        </div>

        <!-- `CallControls`' own root (`Card`) is `position: absolute` (see CallControls.vue), anchored
             to *this* wrapper. Two things had to be fixed here:
             1. This wrapper used to be a normal-flow flex-column item, sitting right after
                `CallStage`/below the also-animating `CallHeader`. Since `CallHeader` hides via a
                `max-height` transition — a layout-affecting property — every frame of *that*
                animation forces the whole flex column (this wrapper included) to reflow, which
                fought with this element's own opacity/transform animation. Taking it out of flow
                entirely (`absolute inset-x-0 bottom-0` against the root container, which the Card
                already assumed anchoring to) decouples it from `CallHeader`'s reflow completely.
             2. `transform-gpu` + `will-change` promote it to its own compositor layer — without them,
                animating opacity/transform on an ancestor of `CallControls`' `backdrop-blur-xl` Card
                makes WebKit resample that blur every frame, which is what made the hide/show
                noticeably janky on-device even before issue 1 was found. -->
        <div :class="[
      isFullscreen
        ? 'absolute bottom-8 left-1/2 -translate-x-1/2 z-30 opacity-0 hover:opacity-100 focus-within:opacity-100 transition-all duration-300 transform translate-y-4 hover:translate-y-0 p-4 rounded-2xl bg-background/40 backdrop-blur-xl border border-white/10 shadow-2xl scale-90 hover:scale-100'
        : ['absolute inset-x-0 bottom-0 z-30 transform-gpu transition-[opacity,transform] duration-300 ease-in-out will-change-[opacity,transform]', (!isMobile || controlsVisible) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none']
    ]">
          <CallControls
              :isMuted="isMicMuted"
              :isDeafened="isDeafened"
              :isVideoOn="isCameraEnabled"
              :isScreenSharing="isScreenShareEnabled"
              :microphones="liveKitManager.state.microphones"
              :speakers="liveKitManager.state.speakers"
              :cameras="liveKitManager.state.cameras"
              :selectedMic="liveKitManager.state.selectedMicId"
              :selectedSpeaker="liveKitManager.state.selectedSpeakerId"
              :selectedCamera="liveKitManager.state.selectedCameraId"
              :isNoiseSuppressionEnabled="isNoiseSuppressionEnabled"
              :isNoiseSuppressionPending="isNoiseSuppressionPending"
              :isEchoCancellationEnabled="isEchoCancellationEnabled"
              :isSelfMonitoring="isSelfMonitoring"
              :micVolume="micVolume"
              :speakerVolume="speakerVolume"
              :micSensitivity="micSensitivity"
              :videoQuality="videoQuality"
              :screenShareQuality="screenShareQuality"
              :screenShareFps="screenShareFps"
              :isScreenShareAudioEnabled="isScreenShareAudioEnabled"
              :micBlocked="isMicBlocked"
              :cameraBlocked="isCameraBlocked"
              :screenShareBlocked="isScreenShareBlocked"
              @toggleMute="toggleMute"
              @toggleDeaf="toggleDeaf"
              @toggleVideo="toggleVideo"
              @toggleScreenShare="toggleScreenShare"
              @toggleNoiseSuppression="toggleNoiseSuppression"
              @toggleEchoCancellation="toggleEchoCancellation"
              @setSelfMonitoring="setSelfMonitoring"
              @disconnect="emit('disconnect')"
              @openCameraModal="openCameraModal"
              @changeMic="changeMicrophone"
              @changeSpeaker="changeSpeaker"
              @changeCamera="changeCamera"
              @changeMicVolume="(v) => groupsStore.setMicVolume(v)"
              @changeSpeakerVolume="(v) => groupsStore.setSpeakerVolume(v)"
              @changeMicSensitivity="(v) => groupsStore.setMicSensitivity(v)"
              @changeVideoQuality="(q) => groupsStore.setVideoQuality(q)"
              @changeScreenShareQuality="(q) => groupsStore.setScreenShareQuality(q)"
              @changeScreenShareFps="(fps) => groupsStore.setScreenShareFps(fps)"
              @changeScreenSharePreset="changeScreenSharePreset"
              @changeScreenShareAudio="(enabled) => groupsStore.setScreenShareAudioEnabled(enabled)"
          />
        </div>

        <DeviceModal
            :isOpen="cameraModalOpen"
            title="Выберите камеру"
            :devices="liveKitManager.state.cameras"
            :pendingDeviceId="pendingCamera"
            @close="cameraModalOpen = false"
            @select="id => pendingCamera = id"
            @confirm="confirmCamera"
        />

        <PermissionModal
            :isOpen="permissionModalOpen"
            :micDenied="isMicPermissionDenied"
            :cameraDenied="isCameraPermissionDenied"
            @recheck="recheckMediaPermissions"
        />

        <ScreenShareModal
            v-model:open="screenShareModalOpen"
            :cameras="liveKitManager.state.cameras"
            :initial-quality="screenShareQuality"
            @start="startScreenShare"
        />
      </div>
    </ContextMenuTrigger>
    <ContextMenuContent class="w-64">
      <div class="px-2 py-1.5">
        <ToggleGroup
            :model-value="viewMode"
            type="single"
            variant="outline"
            size="sm"
            class="w-full"
            @update:model-value="setViewMode"
        >
          <ToggleGroupItem value="focus" class="flex-1" title="Фокус на говорящем пользователе"
                           aria-label="Фокус на говорящем пользователе">
            <Focus class="size-4"/>
          </ToggleGroupItem>
          <ToggleGroupItem value="grid" class="flex-1" title="Сетка" aria-label="Сетка">
            <LayoutGrid class="size-4"/>
          </ToggleGroupItem>
        </ToggleGroup>
      </div>
      <ContextMenuCheckboxItem
          :model-value="showParticipantsWithoutVideo"
          @update:model-value="setShowParticipantsWithoutVideo"
      >
        Показывать участников без видео
      </ContextMenuCheckboxItem>
    </ContextMenuContent>
  </ContextMenu>
</template>

<style scoped>
.aspect-video {
  aspect-ratio: 16 / 9;
}

</style>
